import { FamilyApplicationResp } from '@/apis/family_application/requests.ts'
import { FAMILY_APPLICATION_URLS } from '@/routing/route_paths.ts'
import { ProgramSettingsResp } from '@/storage/program_settings/requests'
import {
  ExtendedApplicationStep,
  getUniqueApplicationSteps,
} from '../constants'
import { validateIncomeVerification } from './income-step-checker'
import { findMissingRequiredKeys } from './step-checker.utils'
import { validateStudents } from './student_step_checker'
import { isV1RenewalApplication } from '@/app/utils/student/student.utils'
import { getIdentityVerificationUrl } from '@/routing/family_application_route_paths.ts'

export type ValidatedStep = {
  status: 'completed' | 'not_completed' | 'warning'
  urls: string[]
  missingFields: string[]
  student_id?: number
}

export type Steps = Record<ExtendedApplicationStep['code'], ValidatedStep>

export const RequiredApplicationData = {
  [FAMILY_APPLICATION_URLS.identity_verification.basic_info]: [
    'first_name',
    'last_name',
    'date_of_birth',
  ],
  [FAMILY_APPLICATION_URLS.residency_verification.index]: [
    'address',
    'city',
    'zip_code',
  ],
  [FAMILY_APPLICATION_URLS.income_verification.index]: [
    'household_inhabitants',
  ],
  [FAMILY_APPLICATION_URLS.income_verification.household_size]: [
    'household_filed_federal_income',
  ],
  [FAMILY_APPLICATION_URLS.income_verification.filing_status]: [
    'household_filed_federal_income',
  ],
  [FAMILY_APPLICATION_URLS.income_verification.social_security_number]: [
    'income_evaluated_by',
  ],
}

const getDefaultStepState = (): ValidatedStep => {
  return {
    urls: [],
    status: 'completed',
    missingFields: [],
  }
}

/**
 * Determines the next unfinished step in the family application process.
 *
 * @param {ApplicationResponse} application - The application response object containing various details required for verification.
 * @param programSettings
 * @return {Steps} - Returns the validation status for each step in the application
 */
export default function validateApplicationSteps(
  application: FamilyApplicationResp,
  programSettings: ProgramSettingsResp
): Steps {
  const activeSteps = getUniqueApplicationSteps(
    programSettings,
    application.students
  )
  const bufferedSteps = activeSteps.reduce((acc, step) => {
    return {
      ...acc,
      [step.code]: getDefaultStepState(),
    }
  }, {} as Steps)

  // START APPLICANT DETAILS

  if (!isV1RenewalApplication(application)) {
    const missingIdentityKeys = findMissingRequiredKeys(
      application,
      RequiredApplicationData[
        FAMILY_APPLICATION_URLS.identity_verification.basic_info
      ]
    )
    bufferedSteps.identityVerification?.missingFields?.push(
      ...missingIdentityKeys
    )

    if (bufferedSteps.identityVerification?.missingFields?.length > 0) {
      bufferedSteps.identityVerification.status = 'not_completed'
      bufferedSteps.identityVerification.urls.push(
        getIdentityVerificationUrl(programSettings)
      )
    }

    const residencyFlags = application.flags?.filter(
      (flag) => flag.flag_code === 'residency' && flag.status !== 'Resolved'
    )

    const missingResidencyKeys = findMissingRequiredKeys(
      application,
      RequiredApplicationData[
        FAMILY_APPLICATION_URLS.residency_verification.index
      ]
    )

    if (
      bufferedSteps.residencyVerification &&
      (missingResidencyKeys.length > 0 ||
        (residencyFlags && residencyFlags.length > 0))
    ) {
      bufferedSteps.residencyVerification.status = 'not_completed'
      bufferedSteps.residencyVerification.urls.push(
        FAMILY_APPLICATION_URLS.residency_verification.index
      )
      bufferedSteps.residencyVerification.missingFields.push(
        ...missingResidencyKeys
      )
    }
  }

  // START STUDENTS
  bufferedSteps.students = validateStudents(application)
  // END STUDENTS

  // START INCOME
  if (typeof bufferedSteps['incomeVerification'] !== 'undefined') {
    bufferedSteps.incomeVerification = validateIncomeVerification(application)
  }
  // END INCOME

  return bufferedSteps
}
